import { faEllipsis } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { lighten, Typography, useTheme } from "@mui/material";
import { isTomorrow } from "date-fns";
import React from "react";
import { useTranslation } from "react-i18next";
import {
  ApproverContainer,
  CardWrapper,
  DateContainer,
  Email,
  EmployeeDataContainer,
  NameContainer,
  PhoneNumber,
  StyledCard,
} from "./styles";
import { CustomDate } from "../../../../models/custom-date/custom-date";
import { TeamMemberDTO } from "../../../../models/team-member/team-member-dto";
import SimplePopover from "../../../../reusable-components/SimplePopover/SimplePopover";
import { intlFormatDateShort } from "../../../../utils/time-utils";

interface Props {
  item: TeamMemberDTO;
  handleNotifyParent: () => void;
}

const TeamMemberCard = ({ item }: Props) => {
  const tRP = "components.team_members.team_member_card";
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <>
      <CardWrapper>
        <StyledCard>
          <EmployeeDataContainer flex={4}>
            <NameContainer>
              <Typography variant="h5">
                {`${item.employee?.lastName} ${item.employee?.firstName}`}

                {item.startDate && isTomorrow(CustomDate.ToDate(item.startDate)) && (
                  <>
                    &nbsp;
                    <span
                      style={{
                        border: `1px solid ${theme.palette.error.main}`,
                        backgroundColor: lighten(theme.palette.error.main, 0.9),
                        borderRadius: "50px",
                        color: theme.palette.error.main,
                        fontSize: "10px",
                        padding: "5px",
                      }}
                    >
                      {" "}
                      {`${t(`${tRP}.starts_tomorrow`)}`}
                    </span>
                  </>
                )}
              </Typography>
              <Email>{item.employee?.email}</Email>
            </NameContainer>
            <PhoneNumber>{item.phoneNumber}</PhoneNumber>
          </EmployeeDataContainer>

          <DateContainer flex={1}>
            <Typography variant="body2">{`${t(`${tRP}.start_date`)}`}</Typography>
            {item.startDate && intlFormatDateShort(CustomDate.ToDate(item.startDate)!)}
          </DateContainer>

          <DateContainer flex={1}>
            <Typography variant="body2">{`${t(`${tRP}.end_date`)}`}</Typography>
            {item.endDate ? (
              intlFormatDateShort(CustomDate.ToDate(item.endDate)!)
            ) : (
              <Typography style={{ fontSize: "10px", color: theme.palette.secondary.light }}>
                <i>{`${t(`${tRP}.no_end_date`)}`}</i>
              </Typography>
            )}
          </DateContainer>

          <ApproverContainer flex={2}>
            {/* <Typography>{`${t(`${tRP}.approver`)}`}</Typography>
            <Typography>{`${item.approver?.lastName} ${item.approver?.firstName}`}</Typography> */}
            <Typography>
              {item.approvers && item.approvers.length > 1
                ? `${t(`components.add_edit_team_member.approvers`)}`
                : `${t(`${tRP}.approver`)}`}
            </Typography>
            {item.approvers && item.approvers.length > 0 ? (
              <>
                {item.approvers.slice(0, 2).map((approver, index) => (
                  <Typography key={approver.id || index}>
                    {`${approver.lastName} ${approver.firstName}`}
                    {index < Math.min(2, item.approvers!.length - 1) && ", "}
                  </Typography>
                ))}
                {item.approvers.length > 2 && (
                  <SimplePopover
                    buttonType="icon"
                    buttonContent={
                      <FontAwesomeIcon
                        icon={faEllipsis}
                        color={theme.palette.primary.main}
                      />
                    }
                    flow="column"
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "center",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "center",
                    }}
                  >
                    {item.approvers.slice(2).map((approver, index) => (
                      <Typography
                        key={approver.id || index + 2}
                        // variant="body2"
                        style={{ padding: "4px 8px", color: theme.palette.primary.main }}
                      >
                        {`${approver.lastName} ${approver.firstName}`}
                      </Typography>
                    ))}
                  </SimplePopover>
                )}
              </>
            ) : (
              <Typography style={{ fontSize: "10px", color: theme.palette.secondary.light }}>
                <i>No approver</i>
              </Typography>
            )}
          </ApproverContainer>
        </StyledCard>
      </CardWrapper>
    </>
  );
};

export default TeamMemberCard;
